import { IkeFieldType } from '@ikegps/core-api';
import { FormModelField, type FormModelFieldState, FormModelMutatorField } from './form.field';

export interface FormModelFieldSelectListState extends FormModelFieldState {
	options: FormModelFieldSelectListOption[];
}

export interface FormModelFieldSelectListOption {
	text: string;
	value: string;
	/**
	 * Color to represent the value
	 * HTML RGB (#ff00ff)
	 */
	color?: string;
}

export class FormModelMutatorSelectList extends FormModelMutatorField<FormModelFieldSelectList> {
	private getOptions() {
		const stateValue = this.field.stateValue;
		if (Array.isArray(stateValue.options)) {
			return JSON.parse(JSON.stringify(stateValue.options));
		}
		return [
			{
				text: FormModelFieldSelectList.UNSELECTED_TEXT,
				value: FormModelFieldSelectList.UNSELECTED_VALUE,
				color: FormModelFieldSelectList.UNSELECTED_COLOR,
			},
		];
	}

	option(option: FormModelFieldSelectListOption, index = -1) {
		const options = this.getOptions();

		if (index === -1 || index > options.length - 1) {
			options.push(option);
		} else {
			options[index] = option;
		}

		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			options,
		});
	}

	remove(index: number) {
		const options = this.getOptions();
		if (index > options.length) {
			throw new Error('Index is out of bounds ' + index + ' > ' + options.length);
		}
		options.splice(index, 1);
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			options,
		});
	}
}

export class FormModelFieldSelectList extends FormModelField<FormModelFieldSelectListState> {
	static isSelectList(field: FormModelField): field is FormModelFieldSelectList {
		return field.type === IkeFieldType.SELECT_LIST;
	}

	private _SLMutator: FormModelMutatorSelectList;
	override get Mutator(): FormModelMutatorSelectList {
		if (this._SLMutator == null) {
			this._SLMutator = new FormModelMutatorSelectList(this);
		}
		return this._SLMutator;
	}

	static UNSELECTED_VALUE = 'unselected';
	static UNSELECTED_TEXT = 'Unselected';
	static UNSELECTED_COLOR = '#a0aec0';

	get options() {
		return (
			this.stateValue.options || [
				{
					text: FormModelFieldSelectList.UNSELECTED_TEXT,
					value: FormModelFieldSelectList.UNSELECTED_VALUE,
					color: FormModelFieldSelectList.UNSELECTED_COLOR,
				},
			]
		);
	}

	/**
	 * Choose the first element in the array as the unselected value
	 * Other wise use the default `unselected`
	 */
	get unselectedValue() {
		const options = this.options;
		if (!Array.isArray(options) || options.length === 0) {
			return FormModelFieldSelectList.UNSELECTED_VALUE;
		}
		return options[0].value;
	}

	createOption(text: string, value: string, color?: string) {
		this.Mutator.option({ text, value, color });
	}

	findOption(value: string | null): FormModelFieldSelectListOption | null {
		const options = this.options;
		for (var i = 0; i < options.length; i++) {
			const opt = options[i];
			if (opt?.value === value) {
				return opt;
			}
		}
		// Unselected option not found, use the default
		if (value === FormModelFieldSelectList.UNSELECTED_VALUE) {
			return {
				text: FormModelFieldSelectList.UNSELECTED_TEXT,
				value: FormModelFieldSelectList.UNSELECTED_VALUE,
				color: FormModelFieldSelectList.UNSELECTED_COLOR,
			};
		}
		return null;
	}
}
